import {
  Card,
  Drawer,
  Empty,
  List,
  Spin,
  Switch,
  Tag,
  notification,
} from "antd";
import { get, isEmpty, isNil } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import { envVarKeysOf as envVarKeys } from "utils/helpers/integrations";

const connectorStatus = (connector, labels) => {
  const total = envVarKeys(connector.configKeys).length;
  const set = (connector.presentConfigKeys || []).length;
  if (connector.enabled === false)
    return { color: "default", text: labels.DISABLED };
  if (total === 0) return { color: "default", text: "—" };
  if (set === 0) return { color: "default", text: labels.NOT_CONFIGURED };
  if (set < total) return { color: "orange", text: `${set}/${total}` };
  return { color: "green", text: labels.CONFIGURED };
};

const ConnectorEditor = ({ connector, labels, onSaved, onClose }) => {
  const [values, setValues] = useState({});
  const [enabled, setEnabled] = useState(connector.enabled !== false);
  const [setParameters, { loading }] = useMutation(
    graphql.mutations.SET_ORGANIZATION_PARAMETERS,
  );

  const keys = envVarKeys(connector.configKeys);
  const present = connector.presentConfigKeys || [];

  const save = async () => {
    const filled = Object.entries(values).reduce(
      (accumulator, [key, value]) => {
        if (value !== "") accumulator[key] = value;
        return accumulator;
      },
      {},
    );
    try {
      await setParameters({
        variables: { values: filled, enabled: { [connector.key]: enabled } },
      });
      notification.success({ message: labels.SAVED });
      onSaved();
    } catch (_) {
      notification.error({ message: labels.SAVE_ERROR });
    }
  };

  return (
    <div>
      <div className="integration-parameters__intro">
        {connector.vendor} · {connector.direction}
      </div>
      <div className="canvas-editor__row mb-16">
        <Switch checked={enabled} onChange={setEnabled} />
        <span>{labels.ENABLED}</span>
      </div>
      {keys.length ? (
        keys.map((key) => (
          <div key={key} className="integration-parameters__field">
            <Input
              label={key}
              value={get(values, key, "")}
              placeholder={present.includes(key) ? "••••••" : labels.NOT_SET}
              onChange={({ target: { value } }) =>
                setValues((previous) => ({ ...previous, [key]: value }))
              }
            />
          </div>
        ))
      ) : (
        <div className="integration-parameters__empty">{labels.NO_KEYS}</div>
      )}
      <div className="integration-parameters__actions">
        <Button type="primary" onClick={save} disabled={loading}>
          {labels.SAVE}
        </Button>
        <Button type="primary--outlined" onClick={onClose}>
          {labels.CANCEL}
        </Button>
      </div>
    </div>
  );
};

const View = () => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.INTEGRATION_PARAMETERS,
  );
  const { user } = useContext(StoreContext);
  const organization = get(user, "organization.id");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const { data, loading, refetch } = useQuery(
    graphql.queries.ORGANIZATION_PARAMETERS,
    { fetchPolicy: "network-only", skip: isEmpty(organization) },
  );

  const connectors = get(data, "organizationParameters") || [];
  const query = search.toLowerCase();
  const filtered = connectors.filter(
    (connector) =>
      isEmpty(query) ||
      (connector.label || "").toLowerCase().includes(query) ||
      (connector.vendor || "").toLowerCase().includes(query) ||
      (connector.key || "").toLowerCase().includes(query) ||
      (connector.configKeys || []).some((key) =>
        (key || "").toLowerCase().includes(query),
      ),
  );
  const labels = t("FORM", { returnObjects: true });

  return (
    <>
      <Header title={t("TITLE")} subtitle={t("SUBTITLE")} actions={[]} />
      <Card type="simple">
        <div className="m-bottom-20 grid--4">
          <Input
            placeholder={t("SEARCH")}
            type="search"
            onChange={({ target: { value } }) => setSearch(value)}
          />
        </div>
        {loading ? (
          <div className="integration-parameters__loading">
            <Spin />
          </div>
        ) : isEmpty(filtered) ? (
          <Empty description={t("EMPTY")} />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={filtered}
            renderItem={(connector) => {
              const status = connectorStatus(connector, labels);
              return (
                <List.Item
                  actions={[
                    <Button
                      key="edit"
                      type="primary--outlined"
                      onClick={() => setEditing(connector)}
                    >
                      {labels.EDIT}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <span>
                        {connector.label} <Tag>{connector.category}</Tag>
                      </span>
                    }
                    description={`${connector.vendor} · ${connector.direction}`}
                  />
                  <Tag color={status.color}>{status.text}</Tag>
                </List.Item>
              );
            }}
          />
        )}
      </Card>
      <Drawer
        title={get(editing, "label")}
        open={!isNil(editing)}
        onClose={() => setEditing(null)}
        width={480}
        destroyOnClose
      >
        {editing ? (
          <ConnectorEditor
            connector={editing}
            labels={labels}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refetch();
            }}
          />
        ) : null}
      </Drawer>
    </>
  );
};

export default View;
